import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { supabase } from '@/shared/lib/supabase';
import type { PaymentRecord } from '@/shared/types';

export interface PaymentFilters {
  status?: string[];
  month?: string | null;
  clientId?: string | null;
}

const mapPayment = (raw: any): PaymentRecord => ({
  id: raw.id,
  user_id: raw.user_id,
  amount: Number(raw.amount ?? 0),
  status: raw.status,
  invoice_url: raw.invoice_url,
  due_date: raw.due_date,
  created_at: raw.created_at,
  description: raw.description,
  workflow_id: raw.workflow_id,
  metadata: raw.metadata ?? null,
  user: raw.user
    ? {
        full_name: raw.user.full_name,
        company_name: raw.user.company_name,
        // email is stored in auth.users; use profile fields in admin listings
        email: raw.user.email || undefined,
      }
    : null,
  product: raw.product ? { name: raw.product.name } : null,
});

export const useAdminPayments = (filters: PaymentFilters) => {
  return useQuery<PaymentRecord[]>({
    queryKey: ['admin-payments', filters],
    queryFn: async () => {
      let query = supabase
        .from('payment_history')
        .select('*, user:user_id(id,full_name,company_name), product:products(id,name)')
        .order('created_at', { ascending: false });

      if (filters.status && filters.status.length) {
        query = query.in('status', filters.status);
      }

      if (filters.clientId) {
        query = query.eq('user_id', filters.clientId);
      }

      if (filters.month) {
        const parsed = dayjs(filters.month, 'YYYY-MM');
        const start = parsed.startOf('month').toISOString();
        const end = parsed.endOf('month').toISOString();
        query = query.gte('created_at', start).lte('created_at', end);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data ?? []).map(mapPayment);
    },
  });
};

export const useUpsertPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<PaymentRecord> & { id?: string }) => {
      if (id) {
        const { data, error } = await supabase
          .from('payment_history')
          .update(payload)
          .eq('id', id)
          .select()
          .single();
        if (error) throw error;
        return mapPayment(data);
      }

      const { data, error } = await supabase
        .from('payment_history')
        .insert(payload)
        .select()
        .single();

      if (error) throw error;
      return mapPayment(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-payments'] });
    },
  });
};

// New hooks for Asaas integration
export function usePayments() {
  return useQuery({
    queryKey: ['admin-payments-asaas'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payments')
        .select(`
          *,
          user:user_id(id, full_name, company_name),
          subscription:subscription_id(id, product:product_id(name))
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useSubscriptions() {
  return useQuery({
    queryKey: ['admin-subscriptions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_subscriptions')
        .select(`
          *,
          user:user_id(id, full_name, company_name),
          product:product_id(name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

